import {
  forwardRef,
  ComponentPropsWithoutRef,
  useRef,
  useImperativeHandle,
} from "react";
import { LayoutUtilProps } from "../../../types";
import { PickerListOption } from "./PickerListOption";
import { useLayoutPropsUtil } from "../../../internal/hooks";

import cx from "classnames";
import styles from "./PickerList.module.scss";

/**
 * Represents an item in the picker list with a label and optional disabled state.
 */
export type PickerListItem = {
  /** The display label for the item */
  label: string;
  /** The value of the item */
  value: number;
  /** Whether the item is disabled and cannot be selected */
  disabled?: boolean;
};

/**
 * Props for the PickerList component
 * @extends ComponentPropsWithoutRef<"ul">
 * @extends LayoutUtilProps
 */
export type PickerListProps = Omit<ComponentPropsWithoutRef<"ul">, "children"> &
  LayoutUtilProps & {
    /** Array of items to render */
    items: PickerListItem[];
    /** Currently selected item (controlled) */
    selected?: PickerListItem;
    /** Callback when selection changes */
    onSelectionChange?: (selected: PickerListItem | undefined) => void;
  };

/**
 * Ref methods exposed by PickerList
 */
export type PickerListRef = {
  /** Scroll to a specific item by label */
  scrollToItem: (label: string, behavior?: ScrollBehavior) => void;
  /** Scroll to a specific item by value */
  scrollToValue: (value: number, behavior?: ScrollBehavior) => void;
};

/**
 * PickerList component for selecting items from a list.
 *
 * Features:
 * - Single selection mode only
 * - Mouse click selection
 * - Programmatic scrolling to specific items
 * - Support for disabled items
 * - Accessible with proper ARIA attributes
 * - Visual consistency with Listbox component
 *
 * @example
 * <PickerList
 *   items={months}
 *   selected={selectedMonth}
 *   onSelectionChange={handleMonthChange}
 * />
 */
export const PickerList = forwardRef<PickerListRef, PickerListProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      className,
      items,
      onSelectionChange,
      selected: selectedProp,
      style,
      ...rest
    } = componentProps;

    const listRef = useRef<HTMLUListElement>(null);

    const scrollToItem = (
      label: string,
      behavior: ScrollBehavior = "smooth",
    ) => {
      const listElement = listRef.current;
      const itemElement = listElement?.querySelector(
        `li[data-item-label="${label}"]`,
      ) as HTMLLIElement | null;

      if (listElement && itemElement) {
        // Calculate the target scroll position to center the item
        const itemTop = itemElement.offsetTop;
        const itemHeight = itemElement.offsetHeight;
        const listHeight = listElement.clientHeight;

        // Center the item vertically in the list
        const targetScrollTop = itemTop - listHeight / 2 + itemHeight / 2;

        // Ensure we don't scroll beyond the bounds
        const maxScrollTop = listElement.scrollHeight - listHeight;
        const clampedScrollTop = Math.max(
          0,
          Math.min(targetScrollTop, maxScrollTop),
        );

        listElement.scrollTo({
          top: clampedScrollTop,
          behavior,
        });
      }
    };

    const scrollToValue = (
      value: number,
      behavior: ScrollBehavior = "smooth",
    ) => {
      const item = items.find((item) => item.value === value);
      if (item) {
        scrollToItem(item.label, behavior);
      }
    };

    useImperativeHandle(ref, () => ({
      scrollToItem,
      scrollToValue,
    }));

    const handleSelectionChange = (item: PickerListItem | undefined) => {
      onSelectionChange?.(item);
    };

    const PickerListClassNames = cx(styles["picker-list"], className);

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <ul
        role="listbox"
        style={styleCombined}
        className={PickerListClassNames}
        data-anv="picker-list"
        ref={listRef}
        {...rest}
        tabIndex={-1}
      >
        {items.map((item) => (
          <PickerListOption
            key={item.label}
            item={item}
            isSelected={selectedProp?.value === item.value}
            onSelectionChange={handleSelectionChange}
          />
        ))}
      </ul>
    );
  },
);

PickerList.displayName = "PickerList";
